import React, { useCallback } from "react";
import { Thermometer, Fan, Activity, Gauge as GaugeIcon, Droplets, Zap, Lightbulb, Wifi, WifiOff, Clock, Volume2 } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { StatCard } from "../common/StatCard";
import { Gauge } from "../common/Gauge";
import { PhaseMini } from "../common/PhaseMini";
import { LcdTerminal } from "../common/LcdTerminal";
import { Panel, Overline } from "../common/Panel";
import { fmt } from "../../lib/api";

const tempColor  = (v) => (v >= 52 ? "var(--danger)" : v >= 44 ? "var(--warning)" : "var(--accent)");
const humColor   = (v) => (v >= 85 ? "var(--danger)" : v >= 70 ? "var(--warning)" : "var(--accent)");
const powerColor = (v) => (v >= 4500 ? "var(--danger)" : v >= 3000 ? "var(--warning)" : "var(--accent)");
const gasColor   = (q) => (q === "DANGER" ? "var(--danger)" : q === "POOR" ? "var(--warning)" : "var(--accent)");

export default function Dashboard() {
  const { telemetry: t, mqttConnected, lastUpdate, pzemOnline } = useApp();

  // When the board goes offline, render all readings as "--" instead of frozen values.
  // Keep `t` (raw telemetry) for status badge / warning banner logic.
  const displayT = pzemOnline ? t : null;

  const ago = lastUpdate
    ? Math.round((Date.now() - lastUpdate.getTime()) / 1000)
    : null;

  const speakReadings = useCallback(() => {
    if (!t || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const L1 = t.phases?.L1, L2 = t.phases?.L2, L3 = t.phases?.L3;
    const txt = [
      `PZEM Energy Board live readings.`,
      L1 ? `Phase L1: ${L1.voltage} volts, ${L1.power} watts, power factor ${L1.pf?.toFixed(2)}.` : "Phase L1: not connected.",
      L2 ? `Phase L2: ${L2.voltage} volts, ${L2.power} watts, power factor ${L2.pf?.toFixed(2)}.` : "Phase L2: not connected.",
      L3 ? `Phase L3: ${L3.voltage} volts, ${L3.power} watts, power factor ${L3.pf?.toFixed(2)}.` : "Phase L3: not connected.",
      `Total power: ${Math.round(t.total_power)} watts.`,
      t.dht_ok ? `Temperature: ${t.dht_temp} degrees Celsius. Humidity: ${t.dht_hum} percent.` : "",
      t.pzem_gas_quality ? `Air quality: ${t.pzem_gas_quality}.` : "",
      `Frequency: ${L1?.hz ?? "--"} hertz.`,
    ].filter(Boolean).join(" ");
    const u = new SpeechSynthesisUtterance(txt);
    u.rate = 0.95;
    window.speechSynthesis.speak(u);
  }, [t]);

  return (
    <div className="space-y-6">
      {/* ── Header + MQTT status ── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Overline>Live Telemetry</Overline>
          <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">PZEM Energy Board</h2>
          <p className="text-xs text-[var(--t2)] font-mono mt-0.5">ESP32-WROOM-32U · 3× PZEM-004T · DHT11 · MQ-135 · PIR · Fan · Light</p>
        </div>
        <div className="flex items-center gap-2">
          {t && (
            <button
              onClick={speakReadings}
              title="Read live readings aloud"
              className="w-9 h-9 rounded-md border border-[var(--bd)] flex items-center justify-center text-[var(--accent)] hover:border-[var(--accent)] transition-colors active:scale-95"
            >
              <Volume2 size={15} />
            </button>
          )}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-mono ${
            !mqttConnected
              ? "border-[var(--danger)]/40 text-[var(--danger)]"
              : t && !pzemOnline
              ? "border-[var(--warning)]/50 text-[var(--warning)]"
              : "border-[var(--accent)]/40 text-[var(--accent)]"
          }`}>
            {mqttConnected ? (t && !pzemOnline ? <WifiOff size={13} /> : <Wifi size={13} />) : <WifiOff size={13} />}
            {!mqttConnected
              ? "MQTT offline"
              : t && !pzemOnline
              ? `Board offline · ${ago != null ? `last data ${ago}s ago` : "stale"}`
              : t
              ? <>Data {ago != null ? `${ago}s ago` : "live"}</>
              : "MQTT live — awaiting board"}
          </div>
        </div>
      </div>

      {/* ── Stale data warning ── */}
      {mqttConnected && t && !pzemOnline && (
        <div className="flex items-start gap-3 p-3.5 rounded-lg border border-[var(--warning)]/40 bg-[var(--warning-bg)]/10 text-sm font-mono text-[var(--warning)]">
          <WifiOff size={15} className="mt-0.5 shrink-0" />
          <span>Board offline — readings below are frozen from {ago != null ? `${ago}s ago` : "the last message"}. Turn on the ESP32 board to resume live telemetry.</span>
        </div>
      )}

      {/* ── PZEM 3-phase readings ── */}
      <div>
        <Overline className="mb-3">3-Phase Power — PZEM-004T × 3</Overline>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center mb-4">
          <StatCard icon={Zap} label="Total Power" value={fmt(displayT?.total_power, 0)} unit="W"
            accent={displayT ? powerColor(displayT.total_power) : "var(--accent)"}
            sub="L1 + L2 + L3" testid="stat-power" />
          <div className="sm:col-span-2 grid grid-cols-1 gap-2 pl-1">
            {["L1","L2","L3"].map((lbl) => {
              const ph = displayT?.phases?.[lbl];
              return (
                <div key={lbl} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <span className="font-mono font-bold text-sm text-[var(--accent)]">{lbl}</span>
                  {ph ? (
                    <>
                      <span className="font-mono text-sm font-semibold text-[var(--t1)]">{ph.voltage}V</span>
                      <span className="font-mono text-sm text-[var(--t2)]">{ph.current}A</span>
                      <span className="font-mono text-sm font-semibold text-[var(--t1)]">{ph.power}W</span>
                      <span className="font-mono text-xs text-[var(--t2)]">PF {ph.pf?.toFixed(2)}</span>
                      <span className="font-mono text-xs text-[var(--t2)]">{ph.hz}Hz</span>
                    </>
                  ) : (
                    <span className="font-mono text-sm text-[var(--t2)]">--</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PhaseMini name="L1" data={displayT?.phases?.L1} testid="phase-mini-l1" />
          <PhaseMini name="L2" data={displayT?.phases?.L2} testid="phase-mini-l2" />
          <PhaseMini name="L3" data={displayT?.phases?.L3} testid="phase-mini-l3" />
        </div>
      </div>

      {/* ── PZEM board environment sensors ── */}
      <div>
        <Overline className="mb-3">Board Sensors — DHT11 · HC-SR501 · MQ-135 · Fan · Light</Overline>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-3">
          <StatCard icon={Thermometer} label="Temperature" value={fmt(displayT?.dht_temp, 1)} unit="°C"
            accent={displayT?.dht_ok ? tempColor(displayT.dht_temp) : "var(--accent)"}
            sub={displayT ? (displayT.dht_ok ? "DHT11 · GPIO 17" : "DHT11 N/A") : "DHT11 · GPIO 17"}
            testid="stat-dht-temp" />
          <StatCard icon={Droplets} label="Humidity" value={fmt(displayT?.dht_hum, 1)} unit="%"
            accent={displayT?.dht_ok ? humColor(displayT.dht_hum) : "var(--accent)"}
            sub={displayT ? (displayT.dht_ok ? "DHT11 · RH" : "DHT11 N/A") : "DHT11 · GPIO 17"}
            testid="stat-hum" />
          <StatCard icon={GaugeIcon} label="Air Quality" value={displayT?.pzem_gas_quality ?? "--"} unit=""
            accent={displayT?.pzem_gas_quality ? gasColor(displayT.pzem_gas_quality) : "var(--accent)"}
            sub={displayT?.pzem_gas_ppm != null ? `${Math.round(displayT.pzem_gas_ppm)} ppm · MQ-135` : "MQ-135 · GPIO 34"}
            testid="stat-gas" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <StatCard icon={Fan} label="Fan Speed" value={fmt(displayT?.pzem_fan_pct, 0)} unit="%"
            sub={displayT ? (displayT.pzem_fan_auto ? "Auto mode" : "Manual") : "GPIO 25 · PWM"}
            testid="stat-fan" />
          <StatCard icon={Activity}
            label={displayT?.pzem_pir ? "Motion Detected" : "PIR"}
            value={displayT ? (displayT.pzem_pir ? "DETECT" : "IDLE") : "--"}
            sub="HC-SR501 · GPIO 4"
            testid="stat-pir" />
          <StatCard icon={Lightbulb} label="Light Hub" value={fmt(displayT?.light_pct, 0)} unit="%"
            sub={displayT ? (displayT.light_pct > 0 ? "ON · IRLZ44N" : "OFF · IRLZ44N") : "GPIO 32 · PWM"}
            testid="stat-light" />
        </div>
      </div>

      {/* ── Gauges + LCD ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Panel className="p-6 md:col-span-2">
          <Overline>Real-time Gauges</Overline>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
            <Gauge value={displayT?.dht_temp ?? null}    max={80}   label="DHT11 Temp"  unit="°C" colorFor={tempColor} testid="gauge-dht-temp" />
            <Gauge value={displayT?.dht_hum ?? null}     max={100}  label="Humidity"    unit="%"  colorFor={humColor}  testid="gauge-hum" />
            <Gauge value={displayT?.total_power ?? null} max={6000} label="Power"       unit="W"  colorFor={powerColor} testid="gauge-power" />
            <Gauge value={displayT ? displayT.power_factor * 100 : null} max={100} label="Pwr Factor" unit="%" testid="gauge-pf" />
          </div>
        </Panel>
        <div className="space-y-3">
          <Overline>Physical LCD</Overline>
          <LcdTerminal telemetry={displayT} testid="lcd-terminal" />
          {displayT?.pzem_ip && (
            <div className="text-[10px] font-mono text-[var(--t2)] flex items-center gap-1.5">
              <Clock size={10} /> IP: {displayT.pzem_ip} · uptime {Math.floor((displayT.pzem_uptime ?? 0) / 60)}m
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
